import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Displays volume pricing information in a popover.
 * Shows quantity rules and pricing tiers with the current tier highlighted.
 * Positioning and hover behavior is handled by the parent anchored-popover-component.
 *
 * @typedef {Object} VolumePricingInfoRefs
 * @property {HTMLElement} popover - The popover element
 *
 * @extends {Component<VolumePricingInfoRefs>}
 */
class VolumePricingInfoComponent extends Component {
  #abortController = new AbortController();

  /**
   * Ensures the parent anchored-popover-component refs are refreshed after connection,
   */
  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.#attachEventListeners();
    this.#syncActiveTier();
    const anchoredPopover = this.closest('anchored-popover-component');
    if (anchoredPopover instanceof Component && anchoredPopover.isConnected) {
      try {
        anchoredPopover.updatedCallback();
      } catch (error) {
        Promise.resolve().then(() => {
          if (anchoredPopover.isConnected) {
            try {
              anchoredPopover.updatedCallback();
            } catch (e) {}
          }
        });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Ensures the parent anchored-popover-component refs are refreshed immediately,
   */
  updatedCallback() {
    super.updatedCallback();
    const anchoredPopover = this.closest('anchored-popover-component');
    if (anchoredPopover instanceof Component) {
      anchoredPopover.updatedCallback();
    }
    this.#syncActiveTier();
  }

  /**
   * Updates the highlighted price tier based on current quantity
   * @param {number} quantity - The current quantity
   */
  updateActiveTier(quantity) {
    const anchoredPopover = this.closest('anchored-popover-component');
    const popover = anchoredPopover instanceof Component ? anchoredPopover.refs?.popover : null;
    const rows = popover?.querySelectorAll('.volume-pricing-info__row[data-quantity]');
    if (!rows) return;

    let activeTier = null;
    for (const row of rows) {
      row.classList.remove('volume-pricing-info__row--active');
      if (row instanceof HTMLElement && quantity >= parseInt(row.dataset.quantity || '0')) {
        activeTier = row;
      }
    }
    activeTier?.classList.add('volume-pricing-info__row--active');
  }

  #attachEventListeners() {
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
  }

  #handleQuantityUpdate = (event) => {
    if (!(event.target instanceof Node)) return;

    const quantityContext = this.#getQuantityContext();
    if (!quantityContext || !quantityContext.contains(event.target)) return;

    this.updateActiveTier(this.#getCurrentQuantity(quantityContext));
  };

  #handleCartUpdate = () => {
    this.#syncActiveTier();
  };

  #syncActiveTier() {
    const quantityContext = this.#getQuantityContext();
    if (!quantityContext) return;

    this.updateActiveTier(this.#getCurrentQuantity(quantityContext));
  }

  #getQuantityContext() {
    return this.closest('product-form-component, .variant-item, .cart-items__table-row, tr');
  }

  #getCurrentQuantity(quantityContext) {
    const productForm = this.closest('product-form-component');
    if (productForm instanceof HTMLElement) {
      const quantityInput = /** @type {HTMLInputElement | null} */ (productForm.querySelector('input[name="quantity"]'));
      if (!quantityInput) return 1;

      const cartQty = parseInt(quantityInput.getAttribute('data-cart-quantity') || '0', 10) || 0;
      const inputQty = parseInt(quantityInput.value || '1', 10) || 1;
      return cartQty + inputQty;
    }

    const quantityInput = /** @type {HTMLInputElement | null} */ (
      quantityContext.querySelector('input[type="number"][data-cart-quantity]')
    );

    if (!quantityInput) return 1;

    return (
      parseInt(quantityInput.value || '', 10)
      || parseInt(quantityInput.getAttribute('value') || '', 10)
      || parseInt(quantityInput.dataset.cartQuantity || '', 10)
      || 1
    );
  }
}

if (!customElements.get('volume-pricing-info')) {
  customElements.define('volume-pricing-info', VolumePricingInfoComponent);
}
